import React, { useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  ArrowOutwardRounded,
  AutoAwesomeRounded,
  BadgeOutlined,
  CloseRounded,
  Diversity3Rounded,
  ExploreRounded,
  FlightTakeoffRounded,
  LanguageRounded,
  LoyaltyRounded,
  SupportAgentRounded,
  RateReviewRounded,
  TimelineRounded,
  TravelExploreRounded
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const statCards = [
  {
    title: 'Tour Management',
    value: 'Destinations',
    caption: 'Keep journeys, destinations, and page content current across the site.',
    icon: TravelExploreRounded,
    tone: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    to: '/manage-continents'
  },
  {
    title: 'Homepage Content',
    value: 'Dynamic Sections',
    caption: 'Control services, value proposition, and the top utility bar from admin.',
    icon: LoyaltyRounded,
    tone: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
    action: 'homepage-modal'
  },
  {
    title: 'Lead Pipeline',
    value: 'Enquiries',
    caption: 'Review incoming tour and travel data without leaving the dashboard.',
    icon: TimelineRounded,
    tone: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)',
    to: '/table/travel_form_submissions/Travel Data/landscape'
  }
];

const quickActions = [
  { label: 'Manage Tours', icon: FlightTakeoffRounded, to: '/manage-continents' },
  { label: 'Discovery', icon: ExploreRounded, to: '/discovery/aaryan_discovery/Discovery/landscape' },
  { label: 'Partners', icon: Diversity3Rounded, to: '/manage-partners' },
  { label: 'Testimonials', icon: RateReviewRounded, to: '/table/aaryan_testimonial/Testimonial/landscape' },
  { label: 'Home Services', icon: LoyaltyRounded, to: '/edit-table/aaryan_home_services/Home Services/landscape' },
  { label: 'Why Choose Us', icon: AutoAwesomeRounded, to: '/edit-table/aaryan_home_why_choose/Why Choose Us/landscape' },
  { label: 'Top Bar', icon: LanguageRounded, to: '/edit-table/aaryan_home_topbar/Home Top Bar/potrait' }
];

const homepageActions = [
  { label: 'Home Services', icon: LoyaltyRounded, to: '/edit-table/aaryan_home_services/Home Services/landscape' },
  { label: 'Why Choose Us', icon: AutoAwesomeRounded, to: '/edit-table/aaryan_home_why_choose/Why Choose Us/landscape' },
  { label: 'Top Bar', icon: SupportAgentRounded, to: '/edit-table/aaryan_home_topbar/Home Top Bar/potrait' },
  { label: 'Testimonials', icon: RateReviewRounded, to: '/table/aaryan_testimonial/Testimonial/landscape' },
  { label: 'Home Banner', icon: FlightTakeoffRounded, to: '/home-mantle' },
  { label: 'Core Competency', icon: BadgeOutlined, to: '/manage-core-competency' }
];

const Dashboard = () => {
  const [homepageModalOpen, setHomepageModalOpen] = useState(false);

  return (
    <Container maxWidth={false} disableGutters>
      <Box
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 6,
          color: '#ffffff',
          background: 'radial-gradient(circle at top left, #38bdf8 0%, #1d4ed8 32%, #0f172a 100%)',
          boxShadow: '0 30px 80px rgba(15, 23, 42, 0.18)'
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h3" sx={{ fontWeight: 800, maxWidth: 760, lineHeight: 1.1 }}>
            Aaryan Leisure & Holidays Control Center
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 760, color: alpha('#ffffff', 0.82), fontWeight: 400 }}>
            Control the content.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} pt={1}>
            <Button
              component={Link}
              to="/manage-continents"
              endIcon={<ArrowOutwardRounded />}
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1.2,
                textTransform: 'none',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                fontWeight: 700,
                '&:hover': {
                  backgroundColor: '#e2e8f0'
                }
              }}
            >
              Open Tour Management
            </Button>
            <Button
              onClick={() => setHomepageModalOpen(true)}
              variant="outlined"
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1.2,
                textTransform: 'none',
                borderColor: alpha('#ffffff', 0.34),
                color: '#ffffff',
                fontWeight: 700,
                '&:hover': {
                  borderColor: '#ffffff',
                  backgroundColor: alpha('#ffffff', 0.08)
                }
              }}
            >
              Edit Homepage Sections
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        {statCards.map((card) => {
          const Icon = card.icon;

          return (
            <Grid item xs={12} md={4} key={card.title}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 5,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 4,
                          display: 'grid',
                          placeItems: 'center',
                          background: card.tone,
                          color: '#ffffff',
                          boxShadow: '0 18px 40px rgba(37, 99, 235, 0.24)'
                        }}
                      >
                        <Icon />
                      </Box>
                      <Button
                        component={card.to ? Link : 'button'}
                        to={card.to || undefined}
                        onClick={card.action === 'homepage-modal' ? () => setHomepageModalOpen(true) : undefined}
                        size="small"
                        sx={{
                          minWidth: 0,
                          borderRadius: 999,
                          color: '#0f172a',
                          textTransform: 'none',
                          fontWeight: 700
                        }}
                        endIcon={<ArrowOutwardRounded sx={{ fontSize: 16 }} />}
                      >
                        Open
                      </Button>
                    </Stack>
                    <Box>
                      <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.12em' }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: '#0f172a' }}>
                        {card.value}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7 }}>
                      {card.caption}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 5,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                Quick Actions
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
                Jump directly into the sections your content team updates most often.
              </Typography>
              <Grid container spacing={2}>
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={3} key={action.label}>
                      <Button
                        component={Link}
                        to={action.to}
                        fullWidth
                        sx={{
                          minHeight: 140,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1.5,
                          borderRadius: 4,
                          px: 2,
                          py: 2.5,
                          textTransform: 'none',
                          backgroundColor: '#f8fafc',
                          borderColor: 'rgba(148, 163, 184, 0.24)',
                          color: '#0f172a',
                          fontWeight: 700,
                          boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.16)',
                          '&:hover': {
                            backgroundColor: alpha('#2563eb', 0.05),
                            boxShadow: 'inset 0 0 0 1px rgba(37, 99, 235, 0.28)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 3.5,
                            display: 'grid',
                            placeItems: 'center',
                            background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                            color: '#ffffff',
                            boxShadow: '0 18px 36px rgba(37, 99, 235, 0.22)'
                          }}
                        >
                          <Icon sx={{ fontSize: 28 }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            lineHeight: 1.35,
                            maxWidth: 120,
                            minHeight: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {action.label}
                        </Typography>
                        <ArrowOutwardRounded sx={{ fontSize: 16, color: '#64748b' }} />
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={homepageModalOpen}
        onClose={() => setHomepageModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Homepage Content Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Open any homepage section from one place and preview the main content controls quickly.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setHomepageModalOpen(false)}
              sx={{
                width: 38,
                height: 38,
                border: '1px solid rgba(148, 163, 184, 0.18)'
              }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Grid container spacing={2}>
            {homepageActions.map((action) => {
              const Icon = action.icon;

              return (
                <Grid item xs={12} sm={6} md={4} key={action.label}>
                  <Button
                    component={Link}
                    to={action.to}
                    onClick={() => setHomepageModalOpen(false)}
                    fullWidth
                    sx={{
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      borderRadius: 4,
                      px: 2,
                      py: 2.5,
                      textTransform: 'none',
                      backgroundColor: '#f8fafc',
                      color: '#0f172a',
                      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.16)',
                      '&:hover': {
                        backgroundColor: alpha('#2563eb', 0.05),
                        boxShadow: 'inset 0 0 0 1px rgba(37, 99, 235, 0.28)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 62,
                        height: 62,
                        borderRadius: 3.5,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                        color: '#ffffff',
                        boxShadow: '0 18px 36px rgba(37, 99, 235, 0.22)'
                      }}
                    >
                      <Icon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        lineHeight: 1.35,
                        maxWidth: 120,
                        minHeight: 38,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {action.label}
                    </Typography>
                    <ArrowOutwardRounded sx={{ fontSize: 16, color: '#64748b' }} />
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
